/**
 * Which configured actions point at a command their adapter no longer exposes?
 *
 * An operator can remove a control write from an adapter's CDO — or delete the
 * key that described it — long after actions were bound to it. The action_banks
 * row survives that, so the monitoring control panel keeps rendering a button
 * that the controller will never accept. Nothing in the UI says so; the button
 * simply does nothing when pressed, which is the hardest kind of fault to
 * report and the easiest to blame on the elevator.
 *
 * LiftCloud's seeder prunes rows it created itself (`auto_seeded = true`), so
 * those self-heal on the next reconcile. Rows the wizard created are written
 * `auto_seeded = false` on purpose — an operator's explicit choice must survive
 * the prune — and those are never cleaned up by anyone. They are the reason
 * this exists.
 *
 * LIMITATION, deliberately not hidden: `auto_seeded` is not carried on the
 * ActionBank FlatBuffer schema, so the frontend cannot yet tell the two apart
 * and flags both. A row the server is about to prune may therefore light up
 * until the next reconcile. Narrowing this needs `auto_seeded: bool` added to
 * `archive/action.fbs` and propagated to its consumers; see the note on
 * `orphanedActionIds`.
 */

/** The subset of an action row this module needs to judge it. */
export interface OrphanCandidateRow {
  id: string;
  bankId?: string;
  messageType: string;
}

/**
 * Ids of the given bank's rows whose command is not in `exposedCommandNames`.
 *
 * Returns an empty array — never a partial one — when `exposed` is empty.
 * An empty set almost always means the adapter's CDO failed to decode or has
 * not loaded yet, not that the adapter genuinely exposes nothing, and flagging
 * every action on that basis would tell the operator their whole bank is broken
 * at the exact moment the app knows least. The predecessor of this code guarded
 * a cascading DELETE the same way, for the same reason, with rather more at
 * stake.
 *
 * Rows with a blank `messageType` are never flagged: they carry no command to
 * match, so their absence from the exposed set means nothing.
 */
export function orphanedActionIds(
  rows: OrphanCandidateRow[],
  bankId: string,
  exposed: Set<string>,
): string[] {
  if (!bankId || exposed.size === 0) return [];

  return rows
    .filter((row) => (row.bankId ?? "") === bankId)
    .filter((row) => row.messageType && !exposed.has(row.messageType))
    .map((row) => row.id);
}

/**
 * The command names an adapter currently exposes, from the option list the
 * step already builds for its Control Command picker.
 *
 * Options are labelled `Control::EnergizeCarCall`; the row's `messageType` is
 * the bare enum name. Stripping the prefix here keeps that coupling in one
 * place rather than at every comparison.
 */
export function exposedCommandNames(options: { label: string }[] | undefined): Set<string> {
  if (!options) return new Set();

  return new Set(
    options
      .map((option) => option.label.replace(/^Control::/, ""))
      .filter((name) => name.length > 0),
  );
}

/** Tooltip shown on a flagged row, naming the fix rather than just the fault. */
export const ORPHAN_TOOLTIP =
  "This bank's adapter no longer exposes this command, so the button will not do " +
  "anything when pressed. Either restore the command on the adapter, or delete " +
  "this action.";
